import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import { openSourceResult } from '../lib/marketImageSearch';
import { sourceImageUrl } from '../lib/marketDb';
import { formatSar, toSar } from '../lib/marketPricing';
import MarketShell from '../components/market/MarketShell';
import { ServicesPanel } from './MarketHome';

// /market/prepare/:ref — a supplier result the customer just opened
// (market-source-search). The page shows at once what the result card already
// had — picture, Arabic title, starting price — while the product is prepared
// (imported, images screened, publication rules) behind it, then becomes the
// product page (replace: Back returns to the results, not here). Nothing names a
// source: the picture comes from market-image by opaque ref.

const T = {
  ar: { market: 'السوق', from: 'من', preparing: 'نجهّز تفاصيل المنتج والخيارات والأسعار…', slow: 'يستغرق التجهيز أحيانًا نصف دقيقة — لحظات وتكتمل الصفحة.',
    notAvailable: 'هذا المنتج غير متاح في معبر حاليًا.', back: 'رجوع للنتائج', noneSub: 'نستطيع توفيره لك من المصانع مباشرة:',
    err: { daily_limit: 'وصلت إلى حد فتح المنتجات لليوم — جرّب غدًا.', busy: 'الخدمة مشغولة الآن — حاول بعد قليل.', not_found: 'انتهت صلاحية هذه النتيجة — ابحث مرة أخرى.', failed: 'تعذّر تجهيز المنتج، حاول مرة أخرى.' } },
  en: { market: 'Market', from: 'From', preparing: 'Preparing the product details, options and prices…', slow: 'Preparing can take half a minute — the page completes in a moment.',
    notAvailable: 'This product is not available on MAABAR right now.', back: 'Back to results', noneSub: 'We can source it for you directly from factories:',
    err: { daily_limit: 'You have reached today\'s limit for opening products — try again tomorrow.', busy: 'The service is busy — try again shortly.', not_found: 'This result has expired — please search again.', failed: 'The product could not be prepared — please try again.' } },
};
T.zh = T.en;

const CARD_KEY = 'mk_src_card:';
/** The result card, kept by SourceResults when it is opened, so this page shows it at once. */
export function keepSourceCard(card) {
  try { sessionStorage.setItem(CARD_KEY + card.ref, JSON.stringify(card)); } catch { /* storage full or blocked: the page shows without it */ }
}
// One preparation per ref, whatever React mounts twice (development) or a reload does.
const inFlight = new Map();

export default function MarketSourcePrepare({ lang = 'ar', user, exchangeRates }) {
  const nav = useNavigate();
  const { ref = '' } = useParams();
  const c = T[lang] || T.ar;
  usePageTitle('market', lang);
  const [card] = useState(() => { try { return JSON.parse(sessionStorage.getItem(CARD_KEY + ref) || 'null'); } catch { return null; } });
  const [state, setState] = useState('preparing');   // preparing | unavailable | error
  const [err, setErr] = useState('');
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!inFlight.has(ref)) inFlight.set(ref, openSourceResult(ref).finally(() => setTimeout(() => inFlight.delete(ref), 60_000)));
    const t = setTimeout(() => { if (alive) setSlow(true); }, 8000);
    inFlight.get(ref).then((r) => {
      if (!alive) return;
      if (r.ok) { nav(`/market/${r.productId}`, { replace: true }); return; }
      if (r.error === 'not_available') setState('unavailable');
      else { setState('error'); setErr(c.err[r.error] || c.err.failed); }
    });
    return () => { alive = false; clearTimeout(t); };
  }, [ref]); // eslint-disable-line react-hooks/exhaustive-deps

  const title = card ? ((lang === 'ar' ? card.title_ar : null) || card.title_en || '') : '';
  const sar = card ? toSar(card.price_cny, exchangeRates) : null;
  return (
    <MarketShell lang={lang} user={user}>
      <div className="mk-wrap" style={{ paddingBottom: 48 }} data-source-prepare={state}>
        <div className="mk-crumbs" aria-label="breadcrumb">
          <button type="button" onClick={() => nav('/market')}>{c.market}</button>
          {title && <><span aria-hidden="true">›</span><span aria-current="page">{title}</span></>}
        </div>
        <div className="mk-imgq" style={{ alignItems: 'flex-start' }}>
          <img src={sourceImageUrl(ref)} alt="" style={{ width: 'min(46vw, 360px)', height: 'auto', aspectRatio: '1 / 1', objectFit: 'cover' }}
            onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
          <div style={{ minWidth: 0 }}>
            {title && <h1 style={{ margin: 0, fontSize: 20 }} dir={lang === 'ar' && card?.title_ar ? 'rtl' : 'ltr'}>{title}</h1>}
            {sar != null && <p className="mk-price" style={{ fontSize: 18 }}>{c.from} {formatSar(sar)}</p>}
            {state === 'preparing' && (
              <>
                <p className="mk-count" data-source-preparing="1" aria-live="polite">{c.preparing}</p>
                <div className="mk-skel" style={{ height: 12, borderRadius: 6, margin: '10px 0' }} />
                <div className="mk-skel" style={{ height: 12, borderRadius: 6, width: '70%' }} />
                {slow && <p className="mk-count" style={{ marginTop: 10 }}>{c.slow}</p>}
              </>
            )}
            {state !== 'preparing' && (
              <>
                <p className="mk-cam-err static" role="alert" data-source-error="1">{state === 'unavailable' ? c.notAvailable : err}</p>
                <button type="button" className="mk-btn ghost" style={{ height: 36 }} onClick={() => nav(-1)}>{c.back}</button>
              </>
            )}
          </div>
        </div>
        {state === 'unavailable' && (
          <div style={{ marginTop: 18 }}>
            <p className="mk-empty" style={{ marginBottom: 8 }}>{c.noneSub}</p>
            <ServicesPanel lang={lang} />
          </div>
        )}
      </div>
    </MarketShell>
  );
}
